import {
  createListSelectors,
  createListSlice,
  ListSliceState,
} from "./helpers/list";
import { createAction } from "@reduxjs/toolkit";
import Client from "../models/Client";
import { UUID } from "../utils";
import { find, findIndex } from "lodash";

const name = "applications";

type SliceState = ListSliceState<
  Client,
  {
    outstandingNotifications: {}[];
    notificationsSettings: {}[];
    failedNotifications: {}[];
    testResponse: string;
  }
>;

const applicationsSlice = createListSlice({
  name,
  initialState: {
    list: [],
    outstandingNotifications: [],
    notificationsSettings: [],
    failedNotifications: [],
    testResponse: "",
  } as SliceState,
  reducers: {
    setOutstandingNotifications: (state, action) => {
      const { id, data }: { id: UUID; data: any } = action.payload;
      const index = findIndex(state.outstandingNotifications, { id });
      state.outstandingNotifications.splice(index, 1, data);
    },
    setFailedNotifications: (state, action) => {
      const { id, data } = action.payload;
      const index = findIndex(state.failedNotifications, { id });
      state.failedNotifications.splice(index, 1, data);
    },
    setNotificationsSettings: (state, action) => {
      const { id, data } = action.payload;
      const index = findIndex(state.notificationsSettings, { id });
      state.notificationsSettings.splice(index, 1, data);
    },
    updateNotificationStatus: (state, action) => {
      const { id, data } = action.payload;
      // only `enable` because `disable` is done on backend after some time
      if (data.status) {
        const item = find(state.notificationsSettings, { id });
        Object.assign(item, { enabled: true });
      }
    },
    setNotificationTestResponse: (state, action) => {
      const { data } = action.payload;
      state.testResponse = data;
    },
  },
});

export const {
  setList,
  setItem,
  updateItem,
  replaceListItem,
  removeListItem,
  setOutstandingNotifications,
  setNotificationsSettings,
  setFailedNotifications,
  updateNotificationStatus,
  setNotificationTestResponse,
} = applicationsSlice.actions;

export const fetchApplications = createAction<{ search?: string } | undefined>(
  "fetchApplications"
);
export const fetchApplication = createAction<{ id: UUID }>("fetchApplication");
export const createApplication = createAction<{ data: Client }>(
  "createApplication"
);
export const updateApplication = createAction<{ id: UUID; data: Client }>(
  "updateApplication"
);
export const deleteApplication = createAction<{ id: UUID }>(
  "deleteApplication"
);
export const generateClientSecret = createAction("generateClientSecret");
export const generateNotificationsSecret = createAction<{ id: UUID }>(
  "generateNotificationsSecret"
);
export const testNotifications = createAction<{ id: UUID }>(
  "testNotifications"
);
export const fetchFailedNotifications = createAction<{ id: UUID }>(
  "fetchNotifications"
);
export const fetchOutstandingNotifications = createAction(
  "fetchOutstandingNotifications"
);
export const fetchNotificationsSettings = createAction(
  "fetchNotificationsSettings"
);
export const setApplicationActiveFlag = createAction(
  "setApplicationActiveFlag"
);
export const updateNotificationsSettings = createAction(
  "updateNotificationsSettings"
);

export const {
  selectList: selectApplications,
  selectItem: selectApplication,
} = createListSelectors<Client>(name);

export const selectOutstandingNotifications = (id: UUID) => (state: any) => {
  return find(state.applications.outstandingNotifications, { clientId: id });
};

export const selectFailedNotifications = (id: UUID) => (state: any) => {
  return find(state.applications.failedNotifications, { clientId: id });
};

export const selectNotificationsSettings = (id: UUID) => (state: any) => {
  return find(state.applications.notificationsSettings, { clientId: id });
};

export const selectNotificationsTestResponse = (state: any) => {
  return state.applications.testResponse;
};

export default applicationsSlice.reducer;
